import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { MessageHistoryCustom, ScenarioProperties } from "@/model";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { useSyncForm } from "@/hook/useSyncForm";
import Button from "@/component/Button";
import { TextInput } from "@/component/input";
import {
    SortableItemWrapper, SortableWrapper,
    toSortableItem, SortableItem, getUpdatedSortableList
} from "@/component/SortableWrapper";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";


interface StatisticsInputGroupProps {
    properties: ScenarioProperties;
    onChange: (scenario: ScenarioProperties) => void;
}

const StatisticsInputGroup: React.FC<StatisticsInputGroupProps> = (props) => {
    const { properties, onChange } = props;

    return (
        <div className="scenario-setting-card-input-group gap-y-6">
            <ConversationFieldListGroup value={properties?.message_history_custom ?? []}
                                        onChange={fields => onChange({ ...properties, message_history_custom: fields })}
            />

            <ContextExcludeKeyListGroup keys={properties?.context_exclude_keys ?? []}
                                        onChange={keys => onChange({ ...properties, context_exclude_keys: keys })}
            />
        </div>
    );
};

interface ConversationFieldListGroupProps {
    value: MessageHistoryCustom[];
    onChange: (fields: MessageHistoryCustom[]) => void;
}

const ConversationFieldListGroup: React.FC<ConversationFieldListGroupProps> = (props) => {
    const { value, onChange } = props;

    const intl = useIntl();

    const [sortableFields, setSortableFields] = useState<SortableItem<MessageHistoryCustom>[]>([]);

    const update = (fields: SortableItem<MessageHistoryCustom>[]) => {
        setSortableFields(fields);
        onChange(fields.map(item => item.data));
    };

    const onClickAdd = () => {
        update([...sortableFields, toSortableItem({ key: '', label: '' })]);
    };

    const onChangeKey = (index: number, key: string) => {
        const prevField = sortableFields[index];
        const newField = { ...prevField, data: { ...prevField.data, key } };
        update(withArray(sortableFields, index, newField));
    };

    const onChangeLabel = (index: number, label: string) => {
        const prevField = sortableFields[index];
        const newField = { ...prevField, data: { ...prevField.data, label } };
        update(withArray(sortableFields, index, newField));
    };

    const onDelete = (index: number) => {
        update(toSpliced(sortableFields, index, 1));
    };

    const syncFromProps = useCallback(() => {
        setSortableFields(getUpdatedSortableList(sortableFields, value));
    }, [value, sortableFields]);

    useSyncForm(syncFromProps);

    return (
        <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">
                {intl.formatMessage({ id: "i10024" })}
            </legend>
            <div className="flex items-center gap-0.5">
                <div className="field-label">
                    {intl.formatMessage({ id: "i10024" })}
                </div>
                <Button variant="text"
                        onClick={onClickAdd}
                >
                    <AddIcon className="size-3.5" />
                    {intl.formatMessage({ id: "b0009" })}
                </Button>
            </div>
            {sortableFields.length > 0 && (
                <SortableWrapper items={sortableFields}
                                 className="flex flex-col gap-1"
                                 onDragSortEnd={update}
                >{sortableFields.map((field, index) => (
                    <SortableItemWrapper key={field.id}
                                         item={field}
                    >{dragHandle => (
                        <div key={field.id} className="w-full flex gap-4 items-start">
                            <div className="label-input-grid-label">
                                {dragHandle}
                            </div>
                            <div className="w-full flex gap-1">
                                <TextInput required
                                           aria-label={`${intl.formatMessage({ id: "b0035" })} ${index + 1}`}
                                           value={field.data.key}
                                           placeholder={intl.formatMessage({ id: "b0035" })}
                                           onChange={e => onChangeKey(index, e.target.value)}
                                />
                                <TextInput required
                                           aria-label={`${intl.formatMessage({ id: "b0036" })} ${index + 1}`}
                                           value={field.data.label}
                                           placeholder={intl.formatMessage({ id: "b0036" })}
                                           onChange={e => onChangeLabel(index, e.target.value)}
                                />
                            </div>
                            <button className="mt-2.5 text-red-500 shrink-0"
                                    aria-label={intl.formatMessage({ id: "b0006" })}
                                    onClick={() => onDelete(index)}
                            >
                                <DeleteIcon className="size-4" />
                            </button>
                        </div>
                    )}</SortableItemWrapper>
                ))}</SortableWrapper>
            )}
        </fieldset>
    );
};

interface ContextExcludeKeyListGroupProps {
    keys: string[];
    onChange: (keys: string[]) => void;
}

const ContextExcludeKeyListGroup: React.FC<ContextExcludeKeyListGroupProps> = (props) => {
    const { keys, onChange } = props;

    const intl = useIntl();

    const [sortableKeys, setSortableKeys] = useState<SortableItem<string>[]>([]);

    const update = (keys: SortableItem<string>[]) => {
        setSortableKeys(keys);
        onChange(keys.map(item => item.data));
    };

    const onChangeKey = (index: number, key: string) => {
        const prevKey = sortableKeys[index];
        const newKey = { ...prevKey, data: key };
        update(withArray(sortableKeys, index, newKey));
    };

    const syncFromProps = useCallback(() => {
        setSortableKeys(getUpdatedSortableList(sortableKeys, keys));
    }, [keys, sortableKeys]);

    useSyncForm(syncFromProps);

    return (
        <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">
                {intl.formatMessage({ id: "i10025" })}
            </legend>
            <div className="flex items-center gap-0.5">
                <div className="field-label">
                    {intl.formatMessage({ id: "i10025" })}
                </div>
                <Button variant="text"
                        onClick={() => update([...sortableKeys, toSortableItem('')])}
                >
                    <AddIcon className="size-3.5" />
                    {intl.formatMessage({ id: "b0009" })}
                </Button>
            </div>
            {sortableKeys.length > 0 && (
                <SortableWrapper items={sortableKeys}
                                 className="flex flex-col gap-1"
                                 onDragSortEnd={update}
                >{sortableKeys.map((key, index) => (
                    <SortableItemWrapper key={key.id}
                                         item={key}
                    >{dragHandle => (
                        <div key={key.id}
                             className="w-full flex gap-4 items-start"
                        >
                            <div className="label-input-grid-label">
                                {dragHandle}
                            </div>
                            <TextInput required
                                       aria-label={`${intl.formatMessage({ id: "i10026" })} ${index + 1}`}
                                       value={key.data}
                                       placeholder={intl.formatMessage({ id: "i10026" })}
                                       onChange={e => onChangeKey(index, e.target.value)}
                            />
                            <button className="mt-2.5 text-red-500 shrink-0"
                                    aria-label={intl.formatMessage({ id: "b0006" })}
                                    onClick={() => update(toSpliced(sortableKeys, index, 1))}
                            >
                                <DeleteIcon className="size-4" />
                            </button>
                        </div>
                    )}</SortableItemWrapper>
                ))}</SortableWrapper>
            )}
        </fieldset>
    );
};

export default StatisticsInputGroup;
